import { useState, useEffect, useCallback } from 'react';
import {
  groupMovements,
  fetchCategories,
  getConvertedTotal,
  fetchSearchOptions,
} from '../api/endpointsBackend';

// Timeline covers 3 full years: (currentYear - 2) through currentYear.
// That range includes the rolling 6-month window and the current year,
// so one request feeds every view on the page.
export const TIMELINE_YEARS = 3;

function buildTimelineRange(now) {
  const endYear = now.getFullYear();
  const startYear = endYear - (TIMELINE_YEARS - 1);
  return {
    start: `${startYear}-01-01`,
    end: `${endYear}-12-31`,
  };
}

/** The current month and the eleven before it: what the currency tabs list (MI-315). */
function buildLastTwelveMonthsRange(now) {
  const pad = (n) => String(n).padStart(2, '0');
  const first = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return {
    start: `${first.getFullYear()}-${pad(first.getMonth() + 1)}-01`,
    end: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(lastDay)}`,
  };
}

/** Movement filter restricting to one source currency; empty means all. */
function currencyFilter(sourceCurrency) {
  if (!sourceCurrency || sourceCurrency === 'all') return [];
  return [{ entity: 'Movement', column: 'currency', operator: 'in', values: [sourceCurrency] }];
}

/** Currency values out of the /movements/filter-options payload. */
function extractCurrencies(response) {
  const entry = (response?.filters || []).find((f) => f.column === 'currency');
  return (entry?.options || []).map((o) => o.value ?? o.displayName).filter(Boolean);
}

/**
 * Reshapes converted buckets into the `/movements/group` bucket shape so every
 * downstream aggregation in useCategoriesView works unchanged. Conversion
 * collapses the currency axis, so all buckets carry the display currency;
 * totals come back signed, which the views already normalise with Math.abs.
 * `is_converted` says whether any of the bucket's movements was in another
 * currency (MI-329): one that was not is exact, and is not marked "≈".
 */
function toGroupShape(buckets, displayCurrency) {
  return (buckets || []).map(b => ({
    year: b.year,
    month: b.month,
    type: b.type,
    category_id: b.category_id,
    currency: displayCurrency,
    total: b.total,
    num_movements: b.num_movements,
    is_converted: b.is_converted,
  }));
}

/**
 * Fetches categories + the monthly bucket grid that feeds the Categories page.
 *
 * Makes exactly 2 requests:
 *   1. GET /categories
 *   2. POST /movements/group with dimensions=['year','month','currency','type','category_id']
 *      over a 3-year window and NO type filter.
 *
 * When `displayCurrency` is set (MI-298), request 2 is swapped for
 * POST /movements/converted-total with the same dimensions minus `currency` —
 * conversion collapses that axis. Each movement is converted at its own date's
 * rate, so every view derived downstream (summary card, donut, category rows,
 * timeline) shares one rate criterion and reconciles with the detail panel.
 *
 * Returns raw buckets; view-level filtering (typeFilter, currency, search)
 * and aggregations happen in useCategoriesView.
 */
export function useCategoriesData(displayCurrency = null, sourceCurrency = 'all') {
  const [categories, setCategories] = useState([]);
  const [monthlyBuckets, setMonthlyBuckets] = useState([]);
  const [currentMonth, setCurrentMonth] = useState({ year: null, month: null });
  const [numUnconvertible, setNumUnconvertible] = useState(0);
  // Per-currency breakdown behind the count, so the UI can say *why* (MI-298).
  const [unconvertibleBuckets, setUnconvertibleBuckets] = useState([]);
  const [periodCurrencies, setPeriodCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      setCurrentMonth({ year: now.getFullYear(), month: now.getMonth() + 1 });

      const { start, end } = buildTimelineRange(now);
      const lastYear = buildLastTwelveMonthsRange(now);

      const [categoriesRes, bucketSource, optionsRes] = await Promise.all([
        fetchCategories(),
        displayCurrency
          // Filtering by source currency has to happen server-side here: the
          // converted buckets carry the *target* currency, so there is nothing
          // left to slice on in the client.
          ? getConvertedTotal(displayCurrency, currencyFilter(sourceCurrency), start, end, [], 'and', {
              dimensions: ['year', 'month', 'type', 'category_id'],
            })
          : groupMovements(
              [],
              ['year', 'month', 'currency', 'type', 'category_id'],
              start,
              end,
            ),
        // The currency-filter tabs list every currency used in the last 12
        // months, and get their own source rather than riding on
        // the bucket payload: converting collapses the currency axis, so a list
        // derived from the buckets would empty out exactly when converted.
        fetchSearchOptions(lastYear.start, lastYear.end),
      ]);

      setCategories(categoriesRes || []);
      setPeriodCurrencies(extractCurrencies(optionsRes));
      if (displayCurrency) {
        setMonthlyBuckets(toGroupShape(bucketSource?.buckets, displayCurrency));
        setNumUnconvertible(bucketSource?.num_unconvertible || 0);
        setUnconvertibleBuckets(bucketSource?.unconvertible || []);
      } else {
        setMonthlyBuckets(bucketSource || []);
        setNumUnconvertible(0);
        setUnconvertibleBuckets([]);
      }
    } catch (err) {
      console.error('Failed to fetch categories data:', err);
      setError('Failed to fetch categories data');
    } finally {
      setLoading(false);
    }
  }, [displayCurrency, sourceCurrency]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    categories,
    monthlyBuckets,
    currentMonth,
    numUnconvertible,
    unconvertibleBuckets,
    periodCurrencies,
    loading,
    error,
    refetch: fetchData,
  };
}
